import { onBeforeUnmount } from 'vue';
import { executionPolling } from '@/utils/pollingService';
import { isDocumentResult } from '@/utils/documentResultValidator';
import axios from 'axios';

export function useExecutionPolling(executionResults, callbacks = {}) {
  // callbacks: { onCompleted, onFailed, onAwaitingSelection, onProgress, notify }

  const self = {};

  self.startPolling = (executionId, resultIndex) => {
    executionPolling.startPolling(
      executionId,
      () => pollExecutionStatus(executionId, resultIndex),
      () => {
        console.log(`Execution ${executionId} completed`);
      },
      (error) => {
        console.error(`Error polling execution ${executionId}:`, error);
      }
    );
  };

  self.stopPolling = (executionId) => {
    executionPolling.stopPolling(executionId);
  };

  self.stopAll = () => {
    executionPolling.stopAll();
  };

  async function pollExecutionStatus(executionId, resultIndex) {
    try {
      const response = await axios.get(`/externalContent/execution/${executionId}/status`);

      if (response.data && response.data.success) {
        const execution = response.data.data;
        const result = executionResults.value[resultIndex];

        if (!result) {
          return;
        }

        let triggerContext = execution.trigger_context;
        if (typeof triggerContext === 'string') {
          try {
            triggerContext = JSON.parse(triggerContext);
          } catch (e) {
            console.error('Failed to parse trigger_context:', e);
            triggerContext = {};
          }
        }

        switch (execution.execution_status) {
          case 'completed': {
            const allOutputs = {};

            if (execution.steps) {
              execution.steps.forEach(step => {
                if (step.outputs) {
                  Object.assign(allOutputs, step.outputs);
                }
              });
            }

            const isPartialRegen = triggerContext?.partial_regeneration === true;
            const targetOutput = triggerContext?.target_output;

            if (isPartialRegen && targetOutput) {
              if (allOutputs.result && typeof allOutputs.result === 'object' && allOutputs.result.populatedData) {
                const newPopulatedData = allOutputs.result.populatedData.find(item => item.type === targetOutput);

                if (newPopulatedData && result.result && result.result.populatedData) {
                  const existingIndex = result.result.populatedData.findIndex(item => item.type === targetOutput);

                  if (existingIndex !== -1) {
                    const updatedPopulatedData = [...result.result.populatedData];
                    updatedPopulatedData[existingIndex] = newPopulatedData;

                    result.result = {
                      ...result.result,
                      populatedData: updatedPopulatedData
                    };
                  } else {
                    console.warn(`Document part type "${targetOutput}" not found in existing data`);
                  }
                }
              } else {
                if (targetOutput === 'result') {
                  result.result = allOutputs.result || result.result;
                } else if (targetOutput === 'reason') {
                  result.reason = allOutputs.reason || result.reason;
                } else {
                  if (!result.additionalOutputs) {
                    result.additionalOutputs = {};
                  }
                  result.additionalOutputs[targetOutput] = allOutputs[targetOutput] || result.additionalOutputs[targetOutput];
                }
              }
            } else {
              result.result = allOutputs.result || '';
              result.reason = allOutputs.reason || '';

              const additionalOutputs = {};
              for (const [key, value] of Object.entries(allOutputs)) {
                if (key !== 'result' && key !== 'reason') {
                  additionalOutputs[key] = value;
                }
              }
              result.additionalOutputs = additionalOutputs;
            }

            const completedResult = {
              ...result,
              success: true,
              processing: false,
              regeneratingSection: null,
              executionId: executionId,
              executionDetails: {
                chain_name: execution.chain_name,
                total_steps: execution.progress.total_steps,
                completed_steps: execution.progress.completed_steps,
                processing_time: execution.completed_at && execution.started_at
                  ? new Date(execution.completed_at) - new Date(execution.started_at)
                  : null
              }
            };

            executionResults.value = [
              ...executionResults.value.slice(0, resultIndex),
              completedResult,
              ...executionResults.value.slice(resultIndex + 1)
            ];

            executionPolling.stopPolling(executionId);

            const regenType = isPartialRegen ? `${targetOutput} section for` : '';
            if (callbacks.notify) {
              callbacks.notify(`${regenType} Row ${result.rowNumber} completed successfully`, 'success');
            }

            if (callbacks.onCompleted) {
              callbacks.onCompleted(completedResult, resultIndex, { isPartialRegen, targetOutput });
            }
            break;
          }

          case 'failed': {
            const failedResult = {
              ...result,
              error: execution.error_message || 'Execution failed',
              success: false,
              processing: false,
              regeneratingSection: null,
              executionId: executionId,
              executionDetails: {
                chain_name: execution.chain_name,
                total_steps: execution.progress.total_steps,
                completed_steps: execution.progress.completed_steps,
                failed_steps: execution.progress.failed_steps,
                error_message: execution.error_message
              }
            };

            executionResults.value = [
              ...executionResults.value.slice(0, resultIndex),
              failedResult,
              ...executionResults.value.slice(resultIndex + 1)
            ];

            executionPolling.stopPolling(executionId);

            if (callbacks.notify) {
              callbacks.notify(`Row ${result.rowNumber} failed: ${execution.error_message}`, 'error');
            }

            if (callbacks.onFailed) {
              callbacks.onFailed(failedResult, resultIndex);
            }
            break;
          }

          case 'queued': {
            const updatedResult = {
              ...result,
              currentStep: 'Queued for generation...',
              progress: null,
              progressDetails: null,
              chainName: execution.chain_name
            };

            executionResults.value = [
              ...executionResults.value.slice(0, resultIndex),
              updatedResult,
              ...executionResults.value.slice(resultIndex + 1)
            ];
            break;
          }

          case 'running': {
            const updates = {
              chainName: execution.chain_name
            };

            if (execution.progress) {
              updates.progress = execution.progress.progress_percentage;
              updates.progressDetails = {
                completed: execution.progress.completed_steps,
                total: execution.progress.total_steps,
                failed: execution.progress.failed_steps
              };
            }

            if (execution.current_step && execution.progress) {
              const currentStepNum = (execution.progress.completed_steps || 0) + 1;
              updates.currentStep = `${execution.current_step.step_name} (${currentStepNum}/${execution.progress.total_steps})`;
            } else if (execution.current_step) {
              updates.currentStep = execution.current_step.step_name;
            } else if (execution.status_description) {
              updates.currentStep = execution.status_description;
            }

            const updatedResult = {
              ...result,
              ...updates
            };

            executionResults.value = [
              ...executionResults.value.slice(0, resultIndex),
              updatedResult,
              ...executionResults.value.slice(resultIndex + 1)
            ];

            if (callbacks.onProgress) {
              callbacks.onProgress(updatedResult, resultIndex);
            }
            break;
          }

          case 'awaiting_selection': {
            let selectionData = execution.selection_data;
            if (typeof selectionData === 'string') {
              try {
                selectionData = JSON.parse(selectionData);
              } catch (e) {
                console.error('Failed to parse selection_data:', e);
                selectionData = null;
              }
            }

            const awaitingResult = {
              ...result,
              currentStep: 'Awaiting user selection...',
              chainName: execution.chain_name,
              awaitingSelection: true,
              selectionData: selectionData
            };

            executionResults.value = [
              ...executionResults.value.slice(0, resultIndex),
              awaitingResult,
              ...executionResults.value.slice(resultIndex + 1)
            ];

            if (selectionData) {
              selectionData.executionId = executionId;
              selectionData.resultIndex = resultIndex;
            }

            executionPolling.stopPolling(executionId);

            if (callbacks.onAwaitingSelection) {
              callbacks.onAwaitingSelection(awaitingResult, selectionData, resultIndex);
            }
            break;
          }
        }
      } else {
        console.error('Failed to get execution status:', response.data);
      }
    } catch (error) {
      console.error('Error polling execution status:', error);
    }
  }

  onBeforeUnmount(() => {
    self.stopAll();
  });

  return self;
}
